"use client";

import { useEffect, useState } from "react";
import { Download, Maximize2, X } from "lucide-react";
import type { OfferLanguage } from "@/db/schema/content-items";

const COPY: Record<OfferLanguage, { enlarge: string; enlargeAria: string; download: string; close: string }> = {
  fr: { enlarge: "Agrandir", enlargeAria: "Agrandir la maquette", download: "Télécharger la maquette", close: "Fermer" },
  en: { enlarge: "Enlarge", enlargeAria: "Enlarge the mockup", download: "Download the mockup", close: "Close" },
};

// Retire les marques diacritiques combinantes (U+0300-U+036F) laissées par
// normalize("NFD") sur les lettres accentuées ; codes numériques plutôt que
// des caractères Unicode littéraux dans le source pour rester sans ambiguïté.
function stripCombiningDiacritics(s: string): string {
  let out = "";
  for (const ch of s) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= 0x0300 && code <= 0x036f) continue;
    out += ch;
  }
  return out;
}

// Nom de fichier proposé au téléchargement, dérivé de la légende (alt) plutôt
// que du chemin technique sur le serveur.
function downloadFilename(alt: string): string {
  const slug = stripCombiningDiacritics(alt.normalize("NFD"))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "maquette"}.png`;
}

// Vignette de maquette + zoom plein écran dans la page (pas de nouvel onglet).
export function MockupZoom({ src, alt, lang = "fr" }: { src: string; alt: string; lang?: OfferLanguage }) {
  const t = COPY[lang];
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t.enlargeAria}
        className="group relative block w-full overflow-hidden rounded-xl border border-line bg-paper-alt shadow-sm"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt={alt} className="h-auto w-full" loading="lazy" />
        <span className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-navy-deep/80 px-3 py-1.5 font-mono text-[0.6rem] uppercase tracking-widest text-paper opacity-0 transition-opacity group-hover:opacity-100">
          <Maximize2 className="h-3 w-3" />
          {t.enlarge}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-auto bg-navy-deep/90 p-4 sm:p-8"
        >
          <div className="fixed right-4 top-4 flex items-center gap-2">
            <a
              href={src}
              download={downloadFilename(alt)}
              onClick={(e) => e.stopPropagation()}
              aria-label={t.download}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-paper/10 text-paper transition-colors hover:bg-paper/20"
            >
              <Download className="h-5 w-5" />
            </a>
            <button
              type="button"
              aria-label={t.close}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-paper/10 text-paper transition-colors hover:bg-paper/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* max-h/max-w (pas w-full) : la maquette doit toujours tenir
              entièrement dans l'écran, jamais dépasser en hauteur et forcer un
              défilement caché sans indice visuel. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[92vw] rounded-lg shadow-2xl sm:max-w-6xl"
          />
        </div>
      )}
    </>
  );
}
